import { DataChangeInfo, Decorators, DeleteResponse, EditorUtils, EntityDialog, EntityGrid, EntityLocalizer, Exception, Fluent, FormKeyAttribute, HandleRouteEvent, IEditDialog, ItemNameAttribute, PropertyGrid, PropertyGridMode, PropertyGridOptions, RetrieveRequest, RetrieveResponse, Router, SaveInitiator, SaveRequest, SaveResponse, ServiceOptions, TabsExtensions, ToolButton, UndeleteResponse, ValidationHelper, Validator, Widget, WidgetProps, confirmDialog, getForm, isMobileView, localText, notifySuccess, serviceCall, stringFormat, tryGetText, validateOptions, validatorAbortHandler } from "@serenity-is/corelib";

/** Auto save types */
export enum AutoSaveOption {
    /** Never auto save */
    Never = 0,
    /** Automatically save pending changes without asking */
    Auto = 1,
    /** Ask for confirmation from user to save pending changes */
    Confirm = 2
}

@Decorators.registerClass('Serenity.EntityGridDialog', [IEditDialog])
export class EntityGridDialog<TItem, P> extends EntityGrid<TItem, P> {
    declare private _entity: TItem;
    declare private _entityId: any;

    declare protected tabs: Fluent;
    declare protected validator: any;
    declare idPrefix: string;
    declare protected dialogPane: Fluent;
    declare protected propertyGrid: PropertyGrid;

    declare protected isDialogOpen: boolean;
    declare protected saveAndCloseButton: Fluent;
    declare protected applyChangesButton: Fluent;
    declare protected deleteButton: Fluent;
    declare protected undeleteButton: Fluent;
    declare protected cloneButton: Fluent;

    declare protected localizer: EntityLocalizer;
    declare protected localizerButton: Fluent;
    declare protected loadedState: string;
    declare protected triedAutoSaveForCurrentEntity: boolean;

    constructor(props: WidgetProps<P>) {
        super(props);

        Fluent.on(this.domNode, 'ondatachange.' + this.uniqueName, () => {
            this.subDialogDataChange();
        });

        Fluent.on(this.domNode, 'handleroute.' + this.uniqueName, (arg: HandleRouteEvent) => {
            if (arg.defaultPrevented)
                return;

            if (!arg.route && this.isDialogOpen) {
                this.dialogClose();
            }
        });

        Fluent.on(window, 'resize.' + this.uniqueName, () => {
            this.arrange();
        });
    }

    protected createSlickGrid() {
        var slickGrid = super.createSlickGrid();

        this.idPrefix = this.uniqueName + '_DLG_';

        this.domNode.classList.add("s-GridWithDialog")

        const id = this.useIdPrefix();

        this.dialogPane = Fluent(
            <div class="s-GridDialogPane flex-layout" id={id.DLG}>
                <div class="s-Form">
                    <form id={id.Form} action="">
                        <div id={id.PropertyGrid}></div>
                    </form>
                </div>
            </div>
        ).appendTo(this.slickContainer);


        this.initValidator();
        this.initTabs();
        this.initPropertyGrid();
        this.initLocalizer();
        return slickGrid;
    }

    public destroy(): void {

        Fluent.off(window, 'resize.' + this.uniqueName);

        if (this.propertyGrid) {
            this.propertyGrid.destroy();
            this.propertyGrid = null;
        }

        if (this.localizer) {
            this.localizer.destroy();
            this.localizer = null;
        }
        this.undeleteButton = null;
        this.applyChangesButton = null;
        this.deleteButton = null;
        this.saveAndCloseButton = null;
        TabsExtensions.destroy(this.tabs);
        this.tabs = null;
        this.validator && this.byId('Form').remove();
        this.validator = null;
        super.destroy();
    }

    protected getItemCssClass(item: TItem, index: number) {
        var klass = super.getItemCssClass(item, index);

        if (this.isDialogOpen && this.entityId == item[this.getIdProperty()]) {
            return (klass || "") + " editing-item";
        }

        return klass;
    }

    getSaveState() {
        try {
            return JSON.stringify(this.getSaveEntity());
        }
        catch (e) {
            return null;
        }
    }

    protected hasPendingChanges() {
        return this.getSaveState() != this.loadedState;
    }

    protected autoSaveOnSwitch(): AutoSaveOption {
        return AutoSaveOption.Never;
    }

    protected autoSaveOnClose(): AutoSaveOption {
        return AutoSaveOption.Never;
    }

    protected checkPendingChangesOnSwitch(entityOrId: any, proceed: () => void) {
        if (this.isDialogOpen &&
            (typeof entityOrId == "number" || typeof entityOrId == "string")) {

            if (entityOrId == this.entityId)
                return;

            if (this.autoSaveOnSwitch() && this.hasPendingChanges()) {

                if (this.autoSaveOnSwitch() == AutoSaveOption.Confirm || this.triedAutoSaveForCurrentEntity) {
                    confirmDialog(localText("Site.Dialogs.PendingChangesConfirmation"),
                        () => {
                            this.save(() => {
                                proceed();
                            });
                        },
                        {
                            onNo: () => {
                                this.loadResponse({});
                                proceed();
                            }
                        });
                }
                else {
                    this.triedAutoSaveForCurrentEntity = true;
                    this.save(() => {
                        proceed();
                    });
                }

                return;
            }
        }

        proceed();
    }

    protected editItem(entityOrId: any) {
        this.checkPendingChangesOnSwitch(entityOrId, () => {
            super.editItem(entityOrId);
        });
    }

    protected createEntityDialog(itemType: string, callback?: (dlg: Widget<any>) => void): Widget<any> | PromiseLike<Widget<any>> {
        // need to fallback here as the subclass may try to create a new dialog, while this one is integrated
        if (itemType == this.getItemType()) {
            callback && callback(this);
            return this;
        }

        return super.createEntityDialog(itemType, callback);
    }

    protected getValidatorOptions(): any {
        return {};
    }

    protected initValidator(): void {
        var form = this.findById('Form');
        if (form instanceof HTMLFormElement) {
            var valOptions = this.getValidatorOptions();
            this.validator = new Validator(form, validateOptions(valOptions));
        }
    }

    protected resetValidation() {
        this.validator && (this.validator as any).resetAll();
    }

    protected validateForm() {
        return this.validator == null || !!this.validator.form();
    }

    public dialogOpen(): void {
        if (!this.isDialogOpen) {
            this.element.toggleClass("dialog-open", true);
            this.isDialogOpen = true;
            this.updateInterface();
            this.layout();
            this.onDialogOpen();

            Router.dialog(this.domNode, this.dialogPane, () => this.getEditHash());
            this.dialogPane.trigger('paneopen');
        }
    }

    protected getEditHash() {
        if (this.entityId)
            return 'edit/' + this.entityId.toString();
        else
            return 'new';
    }

    protected onDialogOpen(): void {
        if (!isMobileView())
            this.dialogPane.findFirst('input:not([type=hidden]), select, textarea').focus();

        this.arrange();
        TabsExtensions.selectTab(this.tabs, 0);
    }

    public arrange(): void {
        if (window.innerWidth < 768) {
            this.dialogPane.findFirst('.panel-titlebar').prependTo(this.domNode);
        }
        else {
            this.element.findFirst('.panel-titlebar').prependTo(this.dialogPane);
        }

        this.dialogPane.findAll('.require-layout').filter(Fluent.isVisibleLike).forEach(el => Fluent.trigger(el, "layout"));
    }

    protected onDialogClose() {
        Fluent.trigger(document, 'click');
    }

    protected getInitialDialogTitle() {
        return "";
    }

    public dialogClose(): void {
        if (this.isDialogOpen) {
            this.triedAutoSaveForCurrentEntity = false;
            this.element.toggleClass("dialog-open", false);
            this.isDialogOpen = false;
            this.localizerButton?.removeClass("pressed");
            this.updateInterface();
            this.layout();
            this.onDialogClose();
            this.dialogPane.trigger('paneclose');
        }
    }

    public get dialogTitle(): string {
        return this.element.data('dialogtitle');
    }

    public set dialogTitle(value: string) {
        var oldTitle = this.dialogTitle;
        this.element.data('dialogtitle', value);
        if (oldTitle != this.dialogTitle) {
            this.setupDialogTitle();
            this.arrange();
        }
    }

    protected setupDialogTitle(): void {
        var value = this.dialogTitle ?? this.getInitialDialogTitle();
        var pt = Fluent(this.dialogPane.findFirst('.panel-titlebar').getNode() ??
            this.element.findFirst('.panel-titlebar').getNode());
        if (!value) {
            pt.remove();
        }
        else {
            if (!pt.length) {
                pt = Fluent(<div class="panel-titlebar"><div class="panel-titlebar-text"></div></div>).prependTo(this.dialogPane);
            }
            pt.findFirst('.panel-titlebar-text').text(value);
            if (!pt.children('.panel-titlebar-close').length) {
                pt.prepend(
                    <button type="button" class="panel-titlebar-close" onClick={e => {
                        if (this.autoSaveOnClose() && this.hasPendingChanges()) {
                            e.preventDefault();

                            if (this.autoSaveOnClose() == AutoSaveOption.Confirm || this.triedAutoSaveForCurrentEntity) {
                                confirmDialog(localText("Site.Dialogs.PendingChangesConfirmation"),
                                    () => this.saveAndCloseButton.trigger("click"),
                                    {
                                        onNo: () => {
                                            this.dialogClose();
                                        }
                                    });
                            }
                            else {
                                this.triedAutoSaveForCurrentEntity = true;
                                this.saveAndCloseButton.trigger("click");
                            }
                        }
                        else
                            this.dialogClose();
                    }} />)
            }
        }
    }

    protected initTabs(): void {
        var tabsDiv = this.findById('Tabs');
        if (!tabsDiv) {
            return;
        }
        this.tabs = TabsExtensions.initialize(tabsDiv, () => this.arrange());
    }

    get entity(): TItem {
        return this._entity;
    }

    protected set entity(value: TItem) {
        this._entity = value || new Object() as any;
    }

    get entityId(): any {
        return this._entityId;
    }

    protected set entityId(value: any) {
        this._entityId = value;
    }

    protected getEntityNameFieldValue(): any {
        return (this.entity[this.getNameProperty()] ?? '').toString();
    }

    protected getEntityTitle(): string {
        if (!this.isEditMode()) {
            return stringFormat(localText('Controls.EntityDialog.NewRecordTitle'), this.getEntitySingular());
        }
        else {
            var title = this.getEntityNameFieldValue() ?? '';
            return stringFormat(localText('Controls.EntityDialog.EditRecordTitle'),
                this.getEntitySingular(), (!title ? '' : (' (' + title + ')')));
        }
    }

    protected updateTitle(): void {
        this.dialogTitle = this.getEntityTitle();
    }

    protected isCloneMode(): boolean {
        return false;
    }

    protected isEditMode(): boolean {
        return this.entityId != null && !this.isCloneMode();
    }

    protected isNew(): boolean {
        return this.entityId == null;
    }

    protected isNewOrDeleted(): boolean {
        return this.isNew() || this.isDeleted();
    }

    protected getDeleteOptions(callback: (response: DeleteResponse) => void): ServiceOptions<DeleteResponse> {
        return {};
    }

    protected deleteHandler(options: ServiceOptions<DeleteResponse>, callback: (response: DeleteResponse) => void): void {
        serviceCall(options);
    }

    protected getDeleteServiceMethod() {
        return this.getService() + '/Delete';
    }

    protected getEntityType(): string {

        var name = super.getEntityType();
        if (name.endsWith('Grid')) {
            this['entityType'] = name = name.substring(0, name.length - 4);
        }

        return name;
    }

    declare private formKey: string;

    protected getFormKey(): string {
        if (this.formKey != null)
            return this.formKey;

        var attr = this.getCustomAttribute(FormKeyAttribute);

        if (attr)
            return (this.formKey = attr.value);

        return (this.formKey = this.getEntityType());
    }

    declare private entitySingular: string;

    protected getEntitySingular(): string {
        if (this.entitySingular != null)
            return this.entitySingular;

        var attr = this.getCustomAttribute(ItemNameAttribute);

        if (attr) {
            this.entitySingular = attr.value;
            this.entitySingular = localText(this.entitySingular, this.entitySingular);
        }
        else {
            var es = tryGetText(this.getLocalTextDbPrefix() + 'EntitySingular');
            if (es == null)
                es = this.getEntityType();
            this.entitySingular = es;
        }

        return this.entitySingular;
    }

    loadNewAndOpenDialog(): void {
        this.loadResponse({});
        this.dialogOpen();
    }

    loadEntityAndOpenDialog(entity: TItem, asPanel?: boolean): void {
        this.loadResponse({ Entity: entity });
        this.dialogOpen();
    }

    protected loadResponse(data: any): void {
        var oldEntityId = this.entityId;
        data = data || {};
        this.onLoadingData(data);
        var entity = data.Entity || new Object();
        this.beforeLoadEntity(entity);
        this.loadEntity(entity);
        this.entity = entity;
        this.afterLoadEntity();
        if (this.isDialogOpen && oldEntityId != this.entityId)
            Router.replaceLast(this.getEditHash(), true);

        if (this.autoSaveOnClose() ||
            this.autoSaveOnSwitch()) {
            this.triedAutoSaveForCurrentEntity = false;
            this.loadedState = this.getSaveState();
        }
    }

    protected loadEntity(entity: TItem): void {
        var idField = this.getIdProperty();

        if (idField != null)
            this.entityId = entity[idField];

        this.entity = entity;

        if (this.propertyGrid != null) {
            this.propertyGrid.set_mode((this.isEditMode() ?
                PropertyGridMode.update : PropertyGridMode.insert));
            this.propertyGrid.load(entity);
        }
    }

    protected beforeLoadEntity(entity: TItem): void {
        this.localizer?.clearValue();
    }

    protected afterLoadEntity(): void {
        this.updateInterface();
        this.updateTitle();
        this.slickGrid.invalidate();
    }

    public loadByIdAndOpenDialog(entityId: any) {
        this.loadById(entityId,
            response => window.setTimeout(() => this.dialogOpen(), 0),
            () => {
                if (!Fluent.isVisibleLike(this.domNode)) {
                    this.element.remove();
                }
            });
    }

    protected onLoadingData(data: RetrieveResponse<TItem>): void {
    }

    protected reloadById(): void {
        this.loadById(this.entityId);
    }

    protected getRetrieveServiceMethod() {
        return this.getService() + '/Retrieve';
    }

    protected initPropertyGrid(): void {
        var pgDiv = this.byId('PropertyGrid');
        if (pgDiv.length <= 0) {
            return;
        }
        var pgOptions = this.getPropertyGridOptions();
        this.propertyGrid = (new PropertyGrid({ element: pgDiv, ...pgOptions })).init();
    }

    protected getFormPropertyItems() {
        var formKey = this.getFormKey();
        return getForm(formKey);
    }

    protected getPropertyGridOptions(): PropertyGridOptions {
        return {
            idPrefix: this.idPrefix,
            items: this.getFormPropertyItems(),
            mode: PropertyGridMode.insert,
            localTextPrefix: 'Forms.' + this.getFormKey() + '.'
        };
    }

    protected validateBeforeSave(): boolean {
        return true;
    }

    protected getCreateServiceMethod() {
        return this.getService() + '/Create';
    }

    protected getUpdateServiceMethod() {
        return this.getService() + '/Update';
    }

    protected getSaveOptions(callback: (response: SaveResponse) => void, initiator?: SaveInitiator): ServiceOptions<SaveResponse> {

        var opt: ServiceOptions<SaveResponse> = {};
        opt.service = this.isEditMode() ? this.getUpdateServiceMethod() : this.getCreateServiceMethod();
        opt.onSuccess = response => {
            this.triedAutoSaveForCurrentEntity = false;
            this.onSaveSuccess(response, initiator);

            callback && callback(response);

            var typ = (this.isEditMode() ? 'update' : 'create');

            var ent = opt.request == null ? null : opt.request.Entity;
            var eid: any = this.isEditMode() ? this.entityId :
                (response == null ? null : response.EntityId);

            var dci: Partial<DataChangeInfo> = {
                operationType: typ,
                entity: ent,
                entityId: eid
            };

            Fluent.trigger(this.domNode, "ondatachange", dci);
        };

        opt.onCleanup = () => {
            this.validator && validatorAbortHandler(this.validator);
        };

        opt.request = this.getSaveRequest();

        return opt;
    }

    protected getSaveEntity(): TItem {

        var entity: TItem = new Object() as any;

        if (this.propertyGrid != null) {
            this.propertyGrid.save(entity);
        }

        if (this.isEditMode()) {
            var idField = this.getIdProperty();
            if (idField != null && entity[idField] == null) {
                entity[idField] = this.entityId;
            }
        }

        return entity;
    }

    protected getSaveRequest(): SaveRequest<TItem> {

        var entity = this.getSaveEntity();
        var req: SaveRequest<TItem> = {};
        req.Entity = entity;

        if (this.isEditMode()) {
            var idField = this.getIdProperty();
            if (idField != null) {
                req.EntityId = this.entityId;
            }
        }

        this.localizer?.editSaveRequest(req);
        return req;
    }

    protected onSaveSuccess(response: SaveResponse, initiator: SaveInitiator): void {
        initiator !== "save-and-close" && this.showSaveSuccessMessage(response, initiator);
    }

    protected save_submitHandler(callback: (response: SaveResponse) => void, initiator: SaveInitiator): void {
        var options = this.getSaveOptions(callback, initiator);
        this.saveHandler(options, callback, initiator);
    }

    protected save(callback?: (response: SaveResponse) => void, initiator?: SaveInitiator): void | boolean {
        return ValidationHelper.submit(this.byId('Form'),
            () => this.validateBeforeSave(),
            () => this.save_submitHandler(callback, initiator));
    }

    protected saveHandler(options: ServiceOptions<SaveResponse>, callback: (response: SaveResponse) => void, initiator: SaveInitiator): void {
        serviceCall(options);
    }

    protected showSaveSuccessMessage(response: SaveResponse, initiator: "save-and-close" | "apply-changes" | "manual"): void {
        notifySuccess(localText('Controls.EntityDialog.SaveSuccessMessage'), '', null);
    }

    protected getButtons(): ToolButton[] {

        return [
            ...super.getButtons(),
            ...EntityDialog.prototype["getToolbarButtons"].call(this).map((x: ToolButton, i: number) => {
                if (i === 0)
                    x.separator = true;
                x.cssClass = Fluent.toClassName([x.cssClass, "dialog-button"]);
                const visible = x.visible;
                x.visible = () => {
                    if (!this.isDialogOpen)
                        return false;
                    if (typeof visible === "function")
                        return visible();
                    return visible ?? true;
                };
                return x;
            })
        ];
    }

    override updateInterface(): void {

        EditorUtils.setContainerReadOnly(this.dialogPane, false);

        var isOpen = !!this.isDialogOpen;
        var hasSavePermission = this.hasSavePermission();
        var viewMode = this.isViewMode();
        var readOnly = this.readOnly;

        this.toolbar?.updateInterface();

        if (this.tabs != null) {
            TabsExtensions.setDisabled(this.tabs, 'Log', !isOpen || this.isNewOrDeleted());
        }

        this.localizer?.updateInterface();

        if (!hasSavePermission || viewMode || readOnly)
            EditorUtils.setContainerReadOnly(this.byId("Form"), true);
    }

    declare protected editClicked: boolean;

    protected getLocalizerOptions() {
        const opt = EntityDialog.prototype["getLocalizerOptions"].call(this);
        opt.getToolButtons = () => this.toolbar.element.findAll(".tool-button.dialog-button");
        return opt;
    }

    protected doDelete(callback: (response: DeleteResponse) => void): void { return EntityDialog.prototype["doDelete"].apply(this, arguments); }
    protected getCloningEntity(): TItem { return EntityDialog.prototype["getCloningEntity"].apply(this, arguments); }
    protected getDeletePermission(): string { return EntityDialog.prototype["getDeletePermission"].apply(this, arguments); }
    protected getInsertPermission(): string { return EntityDialog.prototype["getInsertPermission"].apply(this, arguments); }
    protected getIsDeletedProperty(): string { return EntityDialog.prototype["getIsDeletedProperty"].apply(this, arguments); }
    protected getLanguages(): any[] { return EntityDialog.prototype["getLanguages"].apply(this, arguments); }
    protected getLoadByIdOptions(id: any, callback: (response: RetrieveResponse<TItem>) => void): ServiceOptions<RetrieveResponse<TItem>> { return EntityDialog.prototype["getLoadByIdOptions"].apply(this, arguments); }
    protected getLoadByIdRequest(id: any): RetrieveRequest { return EntityDialog.prototype["getLoadByIdRequest"].apply(this, arguments); }
    protected getNameProperty(): string { return EntityDialog.prototype["getNameProperty"].apply(this, arguments); }
    protected getUpdatePermission(): string { return EntityDialog.prototype["getUpdatePermission"].apply(this, arguments); }
    protected getUndeleteOptions(callback?: (response: UndeleteResponse) => void): ServiceOptions<UndeleteResponse> { return EntityDialog.prototype["getUndeleteOptions"].apply(this, arguments); }
    protected hasDeletePermission() { return EntityDialog.prototype["hasDeletePermission"].apply(this, arguments); }
    protected hasInsertPermission() { return EntityDialog.prototype["hasInsertPermission"].apply(this, arguments); }
    protected hasUpdatePermission() { return EntityDialog.prototype["hasUpdatePermission"].apply(this, arguments); }
    protected hasSavePermission(): boolean { return EntityDialog.prototype["hasSavePermission"].apply(this, arguments); }
    protected initLocalizer(): void { return EntityDialog.prototype["initLocalizer"].apply(this, arguments); }
    protected isDeleted(): boolean { return EntityDialog.prototype["isDeleted"].apply(this, arguments); }
    protected isViewMode() { return EntityDialog.prototype["isViewMode"].apply(this, arguments); }
    load(entityOrId: any, done: () => void, fail?: (ex: Exception) => void): void { return EntityDialog.prototype.load.call(this, entityOrId, done, fail); }
    loadById(id: any, callback?: (response: RetrieveResponse<TItem>) => void, fail?: () => void): void { return EntityDialog.prototype["loadById"].apply(this, arguments); }
    protected loadByIdHandler(options: ServiceOptions<RetrieveResponse<TItem>>, callback: (response: RetrieveResponse<TItem>) => void, fail: () => void): void { return EntityDialog.prototype["loadByIdHandler"].apply(this, arguments); }
    protected onDeleteSuccess(response: DeleteResponse): void { return EntityDialog.prototype["onDeleteSuccess"].apply(this, arguments); }
    protected undelete(callback?: (response: UndeleteResponse) => void): void { return EntityDialog.prototype["undelete"].apply(this, arguments); }
    protected undeleteHandler(options: ServiceOptions<UndeleteResponse>, callback: (response: UndeleteResponse) => void): void { return EntityDialog.prototype["undeleteHandler"].apply(this, arguments); }
    protected useViewMode() { return EntityDialog.prototype["useViewMode"].apply(this, arguments); }
}